import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { supabase } from './supabase';

const TABS = {
  DASHBOARD: 'dashboard',
  DOCUMENTS: 'documents',
  DETAIL: 'detail'
};

const formatMoney = (value, currency = 'USD') => {
  const amount = Number(value || 0);
  return `${currency} ${amount.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
};

const formatDocTypeLabel = (value) => {
  const text = String(value || '').replace(/_/g, ' ').trim();
  if (!text) return '-';
  return text.replace(/\b\w/g, (char) => char.toUpperCase());
};

const deriveStatus = (doc, paidAmount) => {
  const total = Number(doc.total_amount || 0);
  const paid = Number(paidAmount || 0);
  const remaining = Math.max(total - paid, 0);
  const baseStatus = String(doc.status || '').toLowerCase();

  if (baseStatus === 'cancelled') return 'cancelled';
  if (total > 0 && paid >= total - 0.01) return 'paid';
  if (paid > 0 && paid < total) return 'partial';
  if (remaining > 0 && doc.due_date) {
    const dueDate = new Date(doc.due_date);
    const now = new Date();
    if (!Number.isNaN(dueDate.getTime()) && dueDate < now) return 'overdue';
  }
  return baseStatus === 'sent' ? 'sent' : 'confirmed';
};

const FinanceTracker = () => {
  const [activeTab, setActiveTab] = useState(TABS.DASHBOARD);
  const [documents, setDocuments] = useState([]);
  const [paymentsByDoc, setPaymentsByDoc] = useState({});
  const [allPayments, setAllPayments] = useState([]);
  const [selectedDocumentId, setSelectedDocumentId] = useState(null);
  const [selectedDocPayments, setSelectedDocPayments] = useState([]);
  const [selectedDocAttachments, setSelectedDocAttachments] = useState([]);
  const [selectedAttachmentPreview, setSelectedAttachmentPreview] = useState(null);
  const [deletingAttachmentId, setDeletingAttachmentId] = useState(null);
  const [deletingDocumentId, setDeletingDocumentId] = useState(null);
  const [loading, setLoading] = useState(false);
  const [userRole, setUserRole] = useState('employee');

  const [filterText, setFilterText] = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  const [filterCurrency, setFilterCurrency] = useState('');

  const [paymentForm, setPaymentForm] = useState({
    amount: '',
    paymentDate: new Date().toISOString().split('T')[0],
    note: '',
    proofFile: null
  });

  const [attachmentForm, setAttachmentForm] = useState({
    file: null,
    note: ''
  });

  const [noteDraft, setNoteDraft] = useState('');

  const selectedDocument = useMemo(
    () => documents.find((doc) => doc.id === selectedDocumentId) || null,
    [documents, selectedDocumentId]
  );

  const enrichedDocuments = useMemo(() => {
    return documents.map((doc) => {
      const paidAmount = Number(paymentsByDoc[doc.id] || 0);
      const total = Number(doc.total_amount || 0);
      const remaining = Math.max(total - paidAmount, 0);
      const percentPaid = total > 0 ? (paidAmount / total) * 100 : 0;
      const status = deriveStatus(doc, paidAmount);
      return {
        ...doc,
        paidAmount,
        remaining,
        percentPaid,
        computedStatus: status
      };
    });
  }, [documents, paymentsByDoc]);

  const filteredDocuments = useMemo(() => {
    const search = filterText.trim().toLowerCase();
    return enrichedDocuments.filter((doc) => {
      if (search) {
        const haystack = `${doc.document_number || ''} ${doc.party_name || ''} ${doc.title || ''} ${doc.document_type || ''}`.toLowerCase();
        if (!haystack.includes(search)) return false;
      }
      if (filterStatus && doc.computedStatus !== filterStatus) return false;
      if (filterCurrency && doc.currency !== filterCurrency) return false;
      return true;
    });
  }, [enrichedDocuments, filterText, filterStatus, filterCurrency]);

  const loadUserRole = useCallback(async () => {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) return;

    try {
      const { data: userProfile } = await supabase
        .from('user_roles')
        .select('role')
        .eq('id', user.id)
        .single();
      if (String(userProfile?.role || '').toLowerCase() === 'admin') {
        setUserRole('admin');
        return;
      }
    } catch (error) {
      // keep fallback
    }

    const email = String(user.email || '').toLowerCase();
    if (email === 'kerem@atlantismarinepower.com' || email === 'alp@fin-marine.com') {
      setUserRole('admin');
    } else {
      setUserRole('employee');
    }
  }, []);

  const loadDocuments = useCallback(async () => {
    setLoading(true);
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) {
      setLoading(false);
      return;
    }

    let query = supabase
      .from('finance_documents')
      .select('*')
      .order('created_at', { ascending: false });

    if (userRole !== 'admin') {
      query = query.eq('created_by', user.id);
    }

    const { data: docs, error } = await query;
    if (error) {
      console.error('Error loading finance documents:', error);
      setLoading(false);
      return;
    }

    const documentsList = docs || [];
    setDocuments(documentsList);

    if (documentsList.length === 0) {
      setPaymentsByDoc({});
      setAllPayments([]);
      setLoading(false);
      return;
    }

    const docIds = documentsList.map((d) => d.id);
    const { data: paymentRows, error: paymentError } = await supabase
      .from('finance_payments')
      .select('document_id, amount, payment_date')
      .in('document_id', docIds);

    if (paymentError) {
      console.error('Error loading finance payments:', paymentError);
      setLoading(false);
      return;
    }

    const totals = {};
    (paymentRows || []).forEach((row) => {
      totals[row.document_id] = Number(totals[row.document_id] || 0) + Number(row.amount || 0);
    });
    setPaymentsByDoc(totals);
    setAllPayments(paymentRows || []);
    setLoading(false);
  }, [userRole]);

  const loadSelectedDocumentDetails = useCallback(async (documentId) => {
    if (!documentId) return;

    const { data: payments, error: paymentError } = await supabase
      .from('finance_payments')
      .select('*')
      .eq('document_id', documentId)
      .order('payment_date', { ascending: false });

    if (paymentError) {
      console.error('Error loading document payments:', paymentError);
    } else {
      setSelectedDocPayments(payments || []);
    }

    const { data: attachments, error: attachmentError } = await supabase
      .from('finance_attachments')
      .select('*')
      .eq('document_id', documentId)
      .order('created_at', { ascending: false });

    if (attachmentError) {
      console.error('Error loading document attachments:', attachmentError);
    } else {
      const attachmentRows = attachments || [];
      setSelectedDocAttachments(attachmentRows);
      if (attachmentRows.length > 0) {
        const first = attachmentRows[0];
        const { data: urlData } = supabase.storage.from('offer-images').getPublicUrl(first.file_path);
        setSelectedAttachmentPreview({
          id: first.id,
          name: first.file_name,
          type: first.file_type || '',
          url: urlData?.publicUrl || ''
        });
      } else {
        setSelectedAttachmentPreview(null);
      }
    }
  }, []);

  useEffect(() => {
    loadUserRole();
  }, [loadUserRole]);

  useEffect(() => {
    loadDocuments();
  }, [loadDocuments]);

  useEffect(() => {
    if (selectedDocumentId) {
      loadSelectedDocumentDetails(selectedDocumentId);
    }
  }, [selectedDocumentId, loadSelectedDocumentDetails]);

  useEffect(() => {
    if (selectedDocument) {
      setNoteDraft(selectedDocument.notes || '');
    }
  }, [selectedDocument]);

  const updateDocumentStatusFromPayments = async (document) => {
    const { data: paymentRows } = await supabase
      .from('finance_payments')
      .select('amount')
      .eq('document_id', document.id);

    const paid = (paymentRows || []).reduce((sum, row) => sum + Number(row.amount || 0), 0);
    const nextStatus = deriveStatus(document, paid);

    await supabase
      .from('finance_documents')
      .update({ status: nextStatus })
      .eq('id', document.id);
  };

  const uploadToFinancePath = async (documentId, file) => {
    const bucket = 'offer-images';
    const safeFileName = `${Date.now()}-${String(file.name || 'file').replace(/[^a-zA-Z0-9._-]/g, '-')}`;
    const filePath = `finance/${documentId}/${safeFileName}`;

    const { error } = await supabase.storage.from(bucket).upload(filePath, file, {
      cacheControl: '3600',
      upsert: false
    });
    if (error) throw error;

    const { data: urlData } = supabase.storage.from(bucket).getPublicUrl(filePath);
    return {
      bucket,
      filePath,
      publicUrl: urlData?.publicUrl || ''
    };
  };

  const handleAddPayment = async () => {
    if (!selectedDocument || !paymentForm.amount) return;
    const amount = Number(paymentForm.amount);
    if (amount <= 0) return;

    const { data: { user } } = await supabase.auth.getUser();
    if (!user) return;

    const payload = {
      document_id: selectedDocument.id,
      created_by: user.id,
      payment_date: paymentForm.paymentDate || new Date().toISOString().split('T')[0],
      amount,
      note: paymentForm.note || null
    };

    const { data: inserted, error } = await supabase
      .from('finance_payments')
      .insert([payload])
      .select()
      .single();

    if (error) {
      console.error('Error adding payment:', error);
      alert('Failed to add payment');
      return;
    }

    if (paymentForm.proofFile) {
      try {
        const uploaded = await uploadToFinancePath(selectedDocument.id, paymentForm.proofFile);
        await supabase
          .from('finance_attachments')
          .insert([{
            document_id: selectedDocument.id,
            payment_id: inserted.id,
            created_by: user.id,
            file_name: paymentForm.proofFile.name,
            file_path: uploaded.filePath,
            file_type: paymentForm.proofFile.type || 'application/octet-stream',
            note: 'Payment proof'
          }]);
      } catch (uploadError) {
        console.error('Payment proof upload failed:', uploadError);
      }
    }

    await updateDocumentStatusFromPayments(selectedDocument);
    await loadDocuments();
    await loadSelectedDocumentDetails(selectedDocument.id);

    setPaymentForm({
      amount: '',
      paymentDate: new Date().toISOString().split('T')[0],
      note: '',
      proofFile: null
    });
  };

  const handleAddAttachment = async () => {
    if (!selectedDocument || !attachmentForm.file) return;
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) return;

    try {
      const uploaded = await uploadToFinancePath(selectedDocument.id, attachmentForm.file);
      const { error } = await supabase
        .from('finance_attachments')
        .insert([{
          document_id: selectedDocument.id,
          created_by: user.id,
          file_name: attachmentForm.file.name,
          file_path: uploaded.filePath,
          file_type: attachmentForm.file.type || 'application/octet-stream',
          note: attachmentForm.note || null
        }]);

      if (error) throw error;
      await loadSelectedDocumentDetails(selectedDocument.id);
      setAttachmentForm({ file: null, note: '' });
    } catch (error) {
      console.error('Error uploading attachment:', error);
      alert('Failed to upload attachment');
    }
  };

  const handleDeleteAttachment = async (attachment) => {
    if (!selectedDocument || !attachment?.id) return;

    const confirmed = window.confirm(`Are you sure you want to delete attachment "${attachment.file_name}"?`);
    if (!confirmed) return;

    setDeletingAttachmentId(attachment.id);
    try {
      if (attachment.file_path) {
        const { error: storageError } = await supabase.storage
          .from('offer-images')
          .remove([attachment.file_path]);
        if (storageError) {
          console.error('Storage delete error:', storageError);
        }
      }

      const { error } = await supabase
        .from('finance_attachments')
        .delete()
        .eq('id', attachment.id);

      if (error) throw error;

      if (selectedAttachmentPreview?.id === attachment.id) {
        setSelectedAttachmentPreview(null);
      }

      await loadSelectedDocumentDetails(selectedDocument.id);
    } catch (error) {
      console.error('Error deleting attachment:', error);
      alert('Failed to delete attachment');
    } finally {
      setDeletingAttachmentId(null);
    }
  };

  const handleSaveNotes = async () => {
    if (!selectedDocument) return;
    const { error } = await supabase
      .from('finance_documents')
      .update({ notes: noteDraft || null })
      .eq('id', selectedDocument.id);
    if (error) {
      console.error('Error saving notes:', error);
      alert('Failed to save notes');
      return;
    }
    await loadDocuments();
  };

  const handleDeleteDocument = async (doc) => {
    if (!doc?.id) return;
    const confirmed = window.confirm(`Are you sure you want to remove document "${doc.document_number || doc.title || doc.id}" from Finance Tracker?`);
    if (!confirmed) return;

    setDeletingDocumentId(doc.id);
    try {
      const { data: attachments } = await supabase
        .from('finance_attachments')
        .select('file_path')
        .eq('document_id', doc.id);

      const filePaths = (attachments || []).map((a) => a.file_path).filter(Boolean);
      if (filePaths.length > 0) {
        const { error: storageError } = await supabase.storage
          .from('offer-images')
          .remove(filePaths);
        if (storageError) {
          console.error('Storage cleanup warning:', storageError);
        }
      }

      const { error } = await supabase
        .from('finance_documents')
        .delete()
        .eq('id', doc.id);

      if (error) throw error;

      if (selectedDocumentId === doc.id) {
        setSelectedDocumentId(null);
        setSelectedDocPayments([]);
        setSelectedDocAttachments([]);
        setSelectedAttachmentPreview(null);
        setActiveTab(TABS.DOCUMENTS);
      }

      await loadDocuments();
    } catch (error) {
      console.error('Error deleting finance document:', error);
      alert('Failed to delete finance document');
    } finally {
      setDeletingDocumentId(null);
    }
  };

  const summary = useMemo(() => {
    const now = new Date();
    const thirtyDaysAgo = new Date();
    thirtyDaysAgo.setDate(now.getDate() - 30);

    let expectedFromCustomers = 0;
    let toPaySuppliers = 0;
    let overdueCount = 0;
    let overdueAmount = 0;
    let paidThisMonth = 0;

    enrichedDocuments.forEach((doc) => {
      if (doc.computedStatus !== 'paid' && doc.computedStatus !== 'cancelled') {
        if (doc.party_type === 'customer') expectedFromCustomers += doc.remaining;
        if (doc.party_type === 'supplier') toPaySuppliers += doc.remaining;
      }
      if (doc.computedStatus === 'overdue') {
        overdueCount += 1;
        overdueAmount += doc.remaining;
      }
    });

    allPayments.forEach((payment) => {
      const paymentDate = new Date(payment.payment_date);
      if (!Number.isNaN(paymentDate.getTime()) && paymentDate >= thirtyDaysAgo) {
        paidThisMonth += Number(payment.amount || 0);
      }
    });

    return {
      expectedFromCustomers,
      toPaySuppliers,
      overdueCount,
      overdueAmount,
      paidThisMonth
    };
  }, [allPayments, enrichedDocuments]);

  const currencies = useMemo(
    () => [...new Set(enrichedDocuments.map((doc) => doc.currency).filter(Boolean))],
    [enrichedDocuments]
  );

  return (
    <div style={{ padding: '20px', background: '#f7f7f7', minHeight: 'calc(100vh - 60px)' }}>
      <div style={{ maxWidth: '1200px', margin: '0 auto' }}>
        <div style={{ display: 'flex', gap: '10px', marginBottom: '16px', flexWrap: 'wrap' }}>
          <button onClick={() => setActiveTab(TABS.DASHBOARD)} style={{ padding: '8px 14px', borderRadius: 8, border: '1px solid #ddd', background: activeTab === TABS.DASHBOARD ? '#1d4ed8' : '#fff', color: activeTab === TABS.DASHBOARD ? '#fff' : '#111' }}>Dashboard</button>
          <button onClick={() => setActiveTab(TABS.DOCUMENTS)} style={{ padding: '8px 14px', borderRadius: 8, border: '1px solid #ddd', background: activeTab === TABS.DOCUMENTS ? '#1d4ed8' : '#fff', color: activeTab === TABS.DOCUMENTS ? '#fff' : '#111' }}>Documents</button>
          <button onClick={() => setActiveTab(TABS.DETAIL)} disabled={!selectedDocument} style={{ padding: '8px 14px', borderRadius: 8, border: '1px solid #ddd', background: activeTab === TABS.DETAIL ? '#1d4ed8' : '#fff', color: activeTab === TABS.DETAIL ? '#fff' : '#111', opacity: selectedDocument ? 1 : 0.5 }}>Document Detail</button>
        </div>

        {activeTab === TABS.DASHBOARD && (
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: '12px' }}>
            <div style={{ background: '#fff', borderRadius: 12, padding: 16, border: '1px solid #e5e7eb' }}>
              <div style={{ color: '#666', fontSize: 13 }}>Expected from customers</div>
              <div style={{ fontSize: 22, fontWeight: 700 }}>{formatMoney(summary.expectedFromCustomers, 'USD')}</div>
            </div>
            <div style={{ background: '#fff', borderRadius: 12, padding: 16, border: '1px solid #e5e7eb' }}>
              <div style={{ color: '#666', fontSize: 13 }}>To pay suppliers</div>
              <div style={{ fontSize: 22, fontWeight: 700 }}>{formatMoney(summary.toPaySuppliers, 'USD')}</div>
            </div>
            <div style={{ background: '#fff', borderRadius: 12, padding: 16, border: '1px solid #e5e7eb' }}>
              <div style={{ color: '#666', fontSize: 13 }}>Overdue</div>
              <div style={{ fontSize: 22, fontWeight: 700 }}>{summary.overdueCount} docs</div>
              <div style={{ color: '#b91c1c' }}>{formatMoney(summary.overdueAmount, 'USD')}</div>
            </div>
            <div style={{ background: '#fff', borderRadius: 12, padding: 16, border: '1px solid #e5e7eb' }}>
              <div style={{ color: '#666', fontSize: 13 }}>Paid this month</div>
              <div style={{ fontSize: 22, fontWeight: 700 }}>{formatMoney(summary.paidThisMonth, 'USD')}</div>
            </div>
          </div>
        )}

        {activeTab === TABS.DOCUMENTS && (
          <div style={{ background: '#fff', borderRadius: 12, border: '1px solid #e5e7eb', padding: 14 }}>
            <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr 1fr', gap: 8, marginBottom: 12 }}>
              <input value={filterText} onChange={(e) => setFilterText(e.target.value)} placeholder="Search type or party" style={{ padding: 10, border: '1px solid #ddd', borderRadius: 8 }} />
              <select value={filterStatus} onChange={(e) => setFilterStatus(e.target.value)} style={{ padding: 10, border: '1px solid #ddd', borderRadius: 8 }}>
                <option value="">All statuses</option>
                <option value="sent">Sent</option>
                <option value="confirmed">Confirmed</option>
                <option value="partial">Partial</option>
                <option value="paid">Paid</option>
                <option value="cancelled">Cancelled</option>
                <option value="overdue">Overdue</option>
              </select>
              <select value={filterCurrency} onChange={(e) => setFilterCurrency(e.target.value)} style={{ padding: 10, border: '1px solid #ddd', borderRadius: 8 }}>
                <option value="">All currencies</option>
                {currencies.map((currency) => <option key={currency} value={currency}>{currency}</option>)}
              </select>
            </div>

            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                <thead>
                  <tr style={{ background: '#f8fafc' }}>
                    <th style={{ textAlign: 'left', padding: 8 }}>Created</th>
                    <th style={{ textAlign: 'left', padding: 8 }}>Document</th>
                    <th style={{ textAlign: 'left', padding: 8 }}>Type</th>
                    <th style={{ textAlign: 'left', padding: 8 }}>Party</th>
                    <th style={{ textAlign: 'right', padding: 8 }}>Total</th>
                    <th style={{ textAlign: 'right', padding: 8 }}>Paid</th>
                    <th style={{ textAlign: 'right', padding: 8 }}>Remaining</th>
                    <th style={{ textAlign: 'right', padding: 8 }}>% Paid</th>
                    <th style={{ textAlign: 'left', padding: 8 }}>Due</th>
                    <th style={{ textAlign: 'left', padding: 8 }}>Status</th>
                    <th style={{ textAlign: 'left', padding: 8 }}>Action</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredDocuments.map((doc) => (
                    <tr key={doc.id} style={{ borderTop: '1px solid #eee' }}>
                      <td style={{ padding: 8 }}>{new Date(doc.created_at).toLocaleDateString('en-GB')}</td>
                      <td style={{ padding: 8 }}>
                        <button
                          type="button"
                          onClick={() => { setSelectedDocumentId(doc.id); setActiveTab(TABS.DETAIL); }}
                          style={{
                            border: 'none',
                            background: 'none',
                            color: '#1d4ed8',
                            cursor: 'pointer',
                            padding: 0,
                            fontWeight: 600,
                            textDecoration: 'underline'
                          }}
                        >
                          {doc.document_number || doc.title || '-'}
                        </button>
                      </td>
                      <td style={{ padding: 8 }}>{formatDocTypeLabel(doc.document_type)}</td>
                      <td style={{ padding: 8 }}>{doc.party_name}</td>
                      <td style={{ padding: 8, textAlign: 'right' }}>{formatMoney(doc.total_amount, doc.currency)}</td>
                      <td style={{ padding: 8, textAlign: 'right', color: '#166534', fontWeight: 700 }}>
                        {formatMoney(doc.paidAmount, doc.currency)}
                      </td>
                      <td style={{ padding: 8, textAlign: 'right', color: '#991b1b', fontWeight: 700 }}>
                        {formatMoney(doc.remaining, doc.currency)}
                      </td>
                      <td style={{ padding: 8, textAlign: 'right' }}>
                        <span style={{
                          display: 'inline-block',
                          minWidth: 64,
                          textAlign: 'center',
                          padding: '4px 10px',
                          borderRadius: 999,
                          fontSize: 12,
                          fontWeight: 700,
                          background: doc.percentPaid >= 99.99
                            ? '#dcfce7'
                            : doc.percentPaid > 0
                              ? '#fef3c7'
                              : '#fee2e2',
                          color: doc.percentPaid >= 99.99
                            ? '#166534'
                            : doc.percentPaid > 0
                              ? '#92400e'
                              : '#991b1b'
                        }}>
                          {doc.percentPaid.toFixed(1)}%
                        </span>
                      </td>
                      <td style={{ padding: 8 }}>{doc.due_date || '-'}</td>
                      <td style={{ padding: 8 }}>
                        <span style={{
                          textTransform: 'capitalize',
                          padding: '4px 10px',
                          borderRadius: 999,
                          fontSize: 12,
                          fontWeight: 600,
                          background: doc.computedStatus === 'paid'
                            ? '#dcfce7'
                            : doc.computedStatus === 'partial'
                              ? '#fef3c7'
                              : doc.computedStatus === 'overdue'
                                ? '#fee2e2'
                                : '#e5e7eb',
                          color: doc.computedStatus === 'paid'
                            ? '#166534'
                            : doc.computedStatus === 'partial'
                              ? '#92400e'
                              : doc.computedStatus === 'overdue'
                                ? '#991b1b'
                                : '#374151'
                        }}>
                          {doc.computedStatus}
                        </span>
                      </td>
                      <td style={{ padding: 8 }}>
                        <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
                          <button
                            type="button"
                            onClick={() => { setSelectedDocumentId(doc.id); setActiveTab(TABS.DETAIL); }}
                            style={{
                              border: '1px solid #bfdbfe',
                              background: '#eff6ff',
                              color: '#1d4ed8',
                              borderRadius: 6,
                              padding: '6px 10px',
                              cursor: 'pointer',
                              fontSize: 12,
                              fontWeight: 600
                            }}
                          >
                            View Files
                          </button>
                          <button
                            type="button"
                            onClick={() => handleDeleteDocument(doc)}
                            disabled={deletingDocumentId === doc.id}
                            style={{
                              border: '1px solid #fecaca',
                              background: '#fee2e2',
                              color: '#b91c1c',
                              borderRadius: 6,
                              padding: '6px 10px',
                              cursor: deletingDocumentId === doc.id ? 'not-allowed' : 'pointer',
                              fontSize: 12,
                              fontWeight: 600,
                              opacity: deletingDocumentId === doc.id ? 0.6 : 1
                            }}
                          >
                            {deletingDocumentId === doc.id ? 'Deleting...' : 'Remove'}
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {!loading && filteredDocuments.length === 0 && (
                <div style={{ padding: 20, textAlign: 'center', color: '#666' }}>No finance documents found</div>
              )}
            </div>
          </div>
        )}

        {activeTab === TABS.DETAIL && selectedDocument && (
          <div style={{ display: 'grid', gridTemplateColumns: '1.2fr 1fr', gap: 12 }}>
            <div style={{ background: '#fff', borderRadius: 12, border: '1px solid #e5e7eb', padding: 14 }}>
              <h3 style={{ marginTop: 0 }}>Summary</h3>
              {(() => {
                const paidAmount = Number(paymentsByDoc[selectedDocument.id] || 0);
                const total = Number(selectedDocument.total_amount || 0);
                const remaining = Math.max(total - paidAmount, 0);
                const percentPaid = total > 0 ? (paidAmount / total) * 100 : 0;
                const status = deriveStatus(selectedDocument, paidAmount);
                return (
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, minmax(0, 1fr))', gap: 8 }}>
                    <div><strong>Type:</strong> {selectedDocument.document_type}</div>
                    <div><strong>Party:</strong> {selectedDocument.party_name}</div>
                    <div><strong>Total:</strong> {formatMoney(total, selectedDocument.currency)}</div>
                    <div><strong>Paid:</strong> {formatMoney(paidAmount, selectedDocument.currency)}</div>
                    <div><strong>Remaining:</strong> {formatMoney(remaining, selectedDocument.currency)}</div>
                    <div><strong>% Paid:</strong> {percentPaid.toFixed(1)}%</div>
                    <div><strong>Due Date:</strong> {selectedDocument.due_date || '-'}</div>
                    <div><strong>Status:</strong> <span style={{ textTransform: 'capitalize' }}>{status}</span></div>
                  </div>
                );
              })()}

              <hr style={{ margin: '16px 0' }} />
              <h3>Payments</h3>
              <div style={{ display: 'grid', gap: 8, marginBottom: 12 }}>
                <input type="number" placeholder="Amount" value={paymentForm.amount} onChange={(e) => setPaymentForm((p) => ({ ...p, amount: e.target.value }))} style={{ padding: 10, border: '1px solid #ddd', borderRadius: 8 }} />
                <input type="date" value={paymentForm.paymentDate} onChange={(e) => setPaymentForm((p) => ({ ...p, paymentDate: e.target.value }))} style={{ padding: 10, border: '1px solid #ddd', borderRadius: 8 }} />
                <input placeholder="Note (optional)" value={paymentForm.note} onChange={(e) => setPaymentForm((p) => ({ ...p, note: e.target.value }))} style={{ padding: 10, border: '1px solid #ddd', borderRadius: 8 }} />
                <input type="file" accept=".pdf,image/*" onChange={(e) => setPaymentForm((p) => ({ ...p, proofFile: e.target.files?.[0] || null }))} />
                <button onClick={handleAddPayment} style={{ padding: '10px 12px', borderRadius: 8, border: 'none', background: '#1d4ed8', color: '#fff' }}>Add Payment</button>
              </div>
              <div style={{ maxHeight: 220, overflow: 'auto', border: '1px solid #eee', borderRadius: 8 }}>
                {(selectedDocPayments || []).map((payment) => (
                  <div key={payment.id} style={{ padding: 10, borderTop: '1px solid #f1f1f1' }}>
                    <div><strong>{payment.payment_date}</strong> - {formatMoney(payment.amount, selectedDocument.currency)}</div>
                    {payment.note && <div style={{ color: '#666', fontSize: 13 }}>{payment.note}</div>}
                  </div>
                ))}
                {selectedDocPayments.length === 0 && <div style={{ padding: 12, color: '#666' }}>No payments yet</div>}
              </div>
            </div>

            <div style={{ display: 'grid', gap: 12 }}>
              <div style={{ background: '#fff', borderRadius: 12, border: '1px solid #e5e7eb', padding: 14 }}>
                <h3 style={{ marginTop: 0 }}>Attachments</h3>
                <div style={{ display: 'grid', gap: 8, marginBottom: 10 }}>
                  <input type="file" accept=".pdf,image/*" onChange={(e) => setAttachmentForm((p) => ({ ...p, file: e.target.files?.[0] || null }))} />
                  <input placeholder="Attachment note (optional)" value={attachmentForm.note} onChange={(e) => setAttachmentForm((p) => ({ ...p, note: e.target.value }))} style={{ padding: 10, border: '1px solid #ddd', borderRadius: 8 }} />
                  <button onClick={handleAddAttachment} style={{ padding: '10px 12px', borderRadius: 8, border: 'none', background: '#0f766e', color: '#fff' }}>Upload Attachment</button>
                </div>
                <div style={{ maxHeight: 160, overflow: 'auto', border: '1px solid #eee', borderRadius: 8 }}>
                  {selectedDocAttachments.map((attachment) => {
                    const { data: urlData } = supabase.storage.from('offer-images').getPublicUrl(attachment.file_path);
                    return (
                      <div key={attachment.id} style={{ borderTop: '1px solid #f1f1f1', display: 'flex', gap: 8, alignItems: 'center', padding: 8 }}>
                        <button
                          type="button"
                          onClick={() => setSelectedAttachmentPreview({
                            id: attachment.id,
                            name: attachment.file_name,
                            type: attachment.file_type || '',
                            url: urlData?.publicUrl || ''
                          })}
                          style={{
                            border: '1px solid #dbeafe',
                            background: selectedAttachmentPreview?.id === attachment.id ? '#dbeafe' : '#eff6ff',
                            color: '#1d4ed8',
                            borderRadius: 6,
                            padding: '6px 10px',
                            cursor: 'pointer',
                            flex: 1,
                            textAlign: 'left'
                          }}
                        >
                          {attachment.file_name}
                        </button>
                        <a href={urlData?.publicUrl} target="_blank" rel="noreferrer" style={{ color: '#0f766e', fontSize: 12, textDecoration: 'none' }}>
                          Open
                        </a>
                        <button
                          type="button"
                          onClick={() => handleDeleteAttachment(attachment)}
                          disabled={deletingAttachmentId === attachment.id}
                          style={{
                            border: '1px solid #fecaca',
                            background: '#fee2e2',
                            color: '#b91c1c',
                            borderRadius: 6,
                            padding: '6px 8px',
                            cursor: deletingAttachmentId === attachment.id ? 'not-allowed' : 'pointer',
                            fontSize: 12,
                            opacity: deletingAttachmentId === attachment.id ? 0.6 : 1
                          }}
                        >
                          {deletingAttachmentId === attachment.id ? 'Deleting...' : 'Delete'}
                        </button>
                      </div>
                    );
                  })}
                  {selectedDocAttachments.length === 0 && <div style={{ padding: 12, color: '#666' }}>No attachments yet</div>}
                </div>

                {selectedAttachmentPreview?.url && (
                  <div style={{ marginTop: 10, border: '1px solid #e5e7eb', borderRadius: 8, padding: 8, background: '#fafafa' }}>
                    <div style={{ fontSize: 12, color: '#374151', marginBottom: 6, fontWeight: 600 }}>
                      Preview: {selectedAttachmentPreview.name}
                    </div>
                    {String(selectedAttachmentPreview.type || '').toLowerCase().includes('pdf') ? (
                      <iframe
                        title="Attachment Preview"
                        src={selectedAttachmentPreview.url}
                        style={{ width: '100%', height: 260, border: '1px solid #ddd', borderRadius: 6 }}
                      />
                    ) : (
                      <img
                        src={selectedAttachmentPreview.url}
                        alt={selectedAttachmentPreview.name}
                        style={{ width: '100%', maxHeight: 260, objectFit: 'contain', border: '1px solid #ddd', borderRadius: 6, background: 'white' }}
                      />
                    )}
                  </div>
                )}
              </div>

              <div style={{ background: '#fff', borderRadius: 12, border: '1px solid #e5e7eb', padding: 14 }}>
                <h3 style={{ marginTop: 0 }}>Notes</h3>
                <textarea value={noteDraft} onChange={(e) => setNoteDraft(e.target.value)} rows={7} style={{ width: '100%', padding: 10, border: '1px solid #ddd', borderRadius: 8, resize: 'vertical' }} />
                <button onClick={handleSaveNotes} style={{ marginTop: 8, padding: '10px 12px', borderRadius: 8, border: 'none', background: '#111827', color: '#fff' }}>Save Notes</button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default FinanceTracker;
